import {ReactNode} from 'react';
import Box from '@mui/material/Box';
import {SxProps, Theme} from '@mui/material/styles';
import {breakpoint, kit} from './tokens';

interface PageShellProps {
  children: ReactNode;
  // Right rail content. Omit entirely for a single-column page -- the main
  // column then simply runs full width at every size.
  rail?: ReactNode;
  sx?: SxProps<Theme>;
}

// The two-column desktop page frame (web port of the approved `.page` /
// `.main` / `.rail` grid in web-detail-1-quotes.html): a ~740px main column
// beside a rail, capped at 1200px and centered on a mist background.
//
// Responsive per the approved rulebook (web-tab-rules.html):
//  - <768   phone/big-phone: single column, full-width stack.
//  - 768-1023 tablet: still single column -- the rail folds beneath main.
//  - 1024-1279 small desktop: two columns return, rail narrows to 320px, fluid.
//  - >=1280 desktop: content caps at 1200 centered, rail is a fixed 360px.
const PageShell = ({children, rail, sx}: PageShellProps) => (
  <Box
    sx={[
      // Fill the viewport below the 64px top bar so a short page doesn't end
      // in a white void under the mist surface.
      {backgroundColor: kit.mist, minHeight: 'calc(100vh - 64px)'},
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}>
    <Box
      sx={(theme) => ({
        maxWidth: 1200,
        margin: '0 auto',
        padding: '20px 16px 40px',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '20px',
        alignItems: 'start',
        [theme.breakpoints.up('sm')]: {
          padding: '24px 20px 48px',
        },
        [theme.breakpoints.up('md')]: {
          padding: '28px 24px 60px',
          gap: '24px',
          ...(rail ? {gridTemplateColumns: 'minmax(0, 1fr) 320px'} : undefined),
        },
        [theme.breakpoints.up(breakpoint.desktop)]: rail
          ? {gridTemplateColumns: 'minmax(0, 1fr) 360px'}
          : undefined,
      })}>
      <Box
        component="main"
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
        }}>
        {children}
      </Box>
      {rail ? (
        <Box
          component="aside"
          sx={{display: 'flex', flexDirection: 'column', gap: '18px'}}>
          {rail}
        </Box>
      ) : null}
    </Box>
  </Box>
);

export default PageShell;
